package dev.lingvo.site.pages.app.settings.agents

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.lingvo.shared.AgentAutonomy
import dev.lingvo.shared.TokenScope
import dev.lingvo.shared.UpdateAgentLimitsRequest
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LOneTimeSecret
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.Space
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.pages.app.settings.loadWorkspaceProtectedResource
import org.jetbrains.compose.web.dom.Text
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr

/**
 * One agent connection, and everything needed to answer "should this still exist".
 *
 * Its own route because that is the question somebody arrives at from a link — an audit line, an
 * alert, a colleague asking what the release bot is allowed to do. Pause, rotate and revoke live
 * here rather than on the list, so that destroying a credential takes deliberately more than a
 * mis-aimed click on a row.
 *
 * **Rotate is the one to reach for on a leak.** Revoking throws away the scopes, allowlist and
 * budgets somebody reasoned about, and whoever rebuilds them does it from memory.
 */
@Page("/app/settings/agents/{agentId}")
@Layout(".components.layouts.AppLayout")
@Composable
fun AgentDetailPage() {
    val ctx = rememberPageContext()
    val agentId = ctx.route.params["agentId"].orEmpty()
    val workspaceContext = WorkspaceRequestContext.current()
    val agentClaim = WorkspaceActionClaim.capture(workspaceContext, WorkspacePrivilege.AGENTS)
    val pageState = rememberAgentDetailPageState(workspaceContext, agentId)

    with(pageState) {
    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        if (agentClaim == null) {
            PageHeader(t("app.settings.agents.agent_id.connection", "Connection"), "")
            LAlert(
                title = t("app.settings.agents.agent_id.agent_connections_are_managed_by_an", "Agent connections are managed by an owner or admin"),
                description = t("app.settings.agents.agent_id.your_role_in_this_workspace_cannot", "Your role in this workspace cannot view credentials, limits, or agent activity."),
                variant = LAlertVariant.INFO,
            )
            return@Column
        }

        // Read from the list rather than a per-agent endpoint: the credential is never in either
        // response, and one endpoint that cannot leak is better than two that both must not.
        val state = loadWorkspaceProtectedResource(
            context = workspaceContext,
            privilege = WorkspacePrivilege.AGENTS,
            requestKeys = listOf(agentId, refreshKey),
            transport = ApiClient::agents,
        )

        LAsyncContent(
            state,
            rows = 3,
            loadingLabel = t("app.settings.agents.agent_id.loading_the_connection", "Loading the connection"),
            onRetry = { refreshKey++ },
            gap = Space.XL,
        ) { allAgents ->
            val agent = allAgents.orEmpty().firstOrNull { it.id == agentId }
            if (agent == null) {
                PageHeader(t("app.settings.agents.agent_id.connection", "Connection"), "")
                LAlert(
                    t("app.settings.agents.agent_id.this_connection_is_gone", "This connection is gone"),
                    t("app.settings.agents.agent_id.it_was_revoked_or_it_belongs_2", "It was revoked, or it belongs to a different workspace. Revoking is final: " +
                        "the credential stops resolving and cannot be brought back."),
                    variant = LAlertVariant.INFO,
                )
                Link(path = "/app/settings/agents") { Text(t("app.settings.agents.agent_id.back_to_agents", "Back to agents")) }
                return@LAsyncContent
            }

            PageHeader(agent.name, t("app.settings.agents.agent_id.what_it_may_do_here_what", "What it may do here, what it may spend, and how to stop it."))
            agent.clientName?.let { name ->
                AgentDetailMuted(
                    tf(
                        "app.settings.agents.agent_id.reported_by_the_client_as",
                        "Reported by the client as: {name}",
                        "name" to name,
                    ),
                )
            }
            if (agent.expiresAt <= nowIso()) {
                LAlert(
                    t("app.settings.agents.agent_id.this_connection_has_expired", "This connection has expired"),
                    t("app.settings.agents.agent_id.every_call_it_makes_is_refused_2", "Every call it makes is refused until somebody renews it. Nothing was lost — " +
                        "its scopes, projects and budgets are exactly as they were."),
                    variant = LAlertVariant.WARNING,
                )
            }

            rotated?.let { token ->
                LCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                        LAlert(
                            t("app.settings.agents.agent_id.copy_this_now", "Copy this now"),
                            t("app.settings.agents.agent_id.the_old_credential_stopped_working_the_2", "The old credential stopped working the moment this appeared. " +
                                "Scopes, projects and budgets are unchanged."),
                            variant = LAlertVariant.WARNING,
                        )
                        // Lingvo keeps only a hash, so a rotated credential lost here is lost
                        // for good — and the bare `<pre>` this replaces put no copy control
                        // anywhere near it: the only button under it was "Done", which
                        // dismissed the one showing of the value.
                        LOneTimeSecret(
                            value = token,
                            label = tr("app.settings.agents.agent_id.agent_token", "Agent token"),
                            copyLabel = if (copied) {
                                t("app.settings.agents.agent_id.copied", "Copied")
                            } else {
                                t("app.settings.agents.agent_id.copy_token", "Copy token")
                            },
                            onCopied = { message = null; copied = true },
                            onCopyFailed = {
                                message = tr("app.settings.agents.agent_id.couldnt_reach_the_clipboard_select_the_2", "Couldn't reach the clipboard. Select the credential " +
                                    "above and copy it by hand before dismissing this.")
                            },
                            trailingAction = {
                                LButton(
                                    t("app.settings.agents.agent_id.done", "Done"),
                                    { rotated = null; copied = false },
                                    variant = LButtonVariant.SECONDARY,
                                )
                            },
                        )
                    }
                }
            }

            // Only ever here because somebody just pressed something, so a reader who acted
            // should hear it rather than discover it by wandering back up the page.
            message?.let {
                LAlert(
                    t("app.settings.agents.agent_id.that_didnt_work", "That didn't work"),
                    it,
                    variant = LAlertVariant.WARNING,
                    live = true,
                )
            }

            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                    Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                        LSectionTitle(t("app.settings.agents.agent_id.permissions", "Permissions"), Modifier.flexGrow(1))
                        if (agent.paused) LBadge(t("app.settings.agents.agent_id.paused", "paused"), variant = LBadgeVariant.WARNING)
                        LBadge(agent.autonomy.id)
                    }
                    Row(Modifier.fillMaxWidth().gap(Space.XS)) {
                        agent.scopes.forEach { LBadge(it.id) }
                    }
                    // The same catalogued frame the form that created this connection uses. A
                    // Kotlin template is one expression to the extractor and no string at all, so
                    // the em-dash join was fixed in English word order on the page that decides
                    // whether a credential is trusted.
                    agent.scopes.forEach { AgentDetailMuted(agentScopeLine(it)) }
                    AgentDetailMuted(agent.autonomy.description + ".")
                    if (agent.autonomy != AgentAutonomy.PROPOSE) {
                        // Worth repeating on the page where somebody is deciding whether to
                        // narrow it: this is the limit that never moves.
                        AgentDetailMuted(t("app.settings.agents.agent_id.even_so_it_can_never_overwrite", "Even so, it can never overwrite an approved translation."))
                    }
                    AgentDetailMuted(
                        if (agent.projectSlugs.isEmpty()) {
                            t("app.settings.agents.agent_id.every_project_in_this_workspace_including", "Every project in this workspace, including ones created later.")
                        } else {
                            tf(
                                "app.settings.agents.agent_id.only_projects",
                                "Only: {projects}",
                                "projects" to agent.projectSlugs.joinToString(", "),
                            )
                        },
                    )
                    // The thing most likely to have changed underneath somebody.
                    agent.grantingRole?.let { role ->
                        AgentDetailMuted(
                            tf(
                                "app.settings.agents.agent_id.borrows_the_authority_of_a_role",
                                "Borrows the authority of a {role}, as they are right now.",
                                "role" to role.name.lowercase(),
                            ),
                        )
                    } ?: LAlert(
                        t("app.settings.agents.agent_id.the_member_who_created_this_has", "The member who created this has left"),
                        t("app.settings.agents.agent_id.there_is_nothing_left_for_it_2", "There is nothing left for it to borrow authority from, so every call it " +
                            "makes is refused. Create a new connection to replace it."),
                        variant = LAlertVariant.WARNING,
                    )
                }
            }

            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    LSectionTitle(t("app.settings.agents.agent_id.budget", "Budget"))
                    if (!editingBudget) {
                        if (agent.creditBudgetMonthly > 0) {
                            AgentDetailNumbers(
                                tPlural(
                                    "app.settings.agents.agent_id.credits_per_call",
                                    agent.maxCreditsPerCall,
                                    "{count} credit per call",
                                    "{count} credits per call",
                                ),
                            )
                            AgentDetailNumbers(
                                tf(
                                    "app.settings.agents.agent_id.credits_per_day",
                                    "{count} per day",
                                    "count" to agent.creditBudgetDaily,
                                ),
                            )
                            AgentDetailNumbers(
                                tf(
                                    "app.settings.agents.agent_id.credits_per_month",
                                    "{count} per month",
                                    "count" to agent.creditBudgetMonthly,
                                ),
                            )
                            // One literal, not a run joined by `+`: the extractor only reaches a
                            // single-literal `t(…)`, so a sentence split across `+` never
                            // reached the catalogue and could not be translated at all.
                            AgentDetailMuted(t("app.settings.agents.agent_id.per_call_is_a_hard_ceiling_2", "Per call is a hard ceiling — a bigger call is refused and nobody can approve it through. Going over the day or the month raises a request instead."))
                            Link(path = "/app/settings/agents/approvals") { Text(t("app.settings.agents.agent_id.pending_spend_requests", "Pending spend requests")) }
                        } else {
                            AgentDetailMuted(t("app.settings.agents.agent_id.cannot_spend_credits_this_is_the", "Cannot spend credits. This is the default and usually the right answer."))
                        }
                        LButton(
                            t("app.settings.agents.agent_id.change_the_budget", "Change the budget"),
                            {
                                perCall = agent.maxCreditsPerCall.toString()
                                daily = agent.creditBudgetDaily.toString()
                                monthly = agent.creditBudgetMonthly.toString()
                                editingBudget = true
                            },
                            variant = LButtonVariant.SECONDARY,
                        )
                    } else {
                        // Editable in place because a pipeline that grew is the most common
                        // legitimate reason anybody opens this page, and making that a
                        // revoke-and-rebuild is how workspaces end up with a budget of 999999.
                        Row(Modifier.fillMaxWidth().gap(Space.SM)) {
                            LTextField(perCall, { perCall = it }, Modifier.flexGrow(1), label = t("app.settings.agents.agent_id.max_per_call", "Max per call"))
                            LTextField(daily, { daily = it }, Modifier.flexGrow(1), label = t("app.settings.agents.agent_id.per_day", "Per day"))
                            LTextField(monthly, { monthly = it }, Modifier.flexGrow(1), label = t("app.settings.agents.agent_id.per_month", "Per month"))
                        }
                        AgentDetailMuted(t("app.settings.agents.agent_id.scopes_are_not_editable_here_widening", "Scopes are not editable here: widening what it may reach re-mints the credential."))
                        Row(Modifier.gap(Space.SM)) {
                            AgentDetailAction(
                                pageClaim = agentClaim,
                                label = t("app.settings.agents.agent_id.save", "Save"),
                                variant = LButtonVariant.PRIMARY,
                                enabled = !busy,
                                busy = busy,
                                setBusy = { busy = it },
                                showLoading = true,
                                request = { workspaceSlug ->
                                    ApiClient.updateAgentLimits(
                                        workspaceSlug,
                                        agent.id,
                                        UpdateAgentLimitsRequest(
                                            creditBudgetMonthly = monthly.toIntOrNull() ?: 0,
                                            creditBudgetDaily = daily.toIntOrNull() ?: 0,
                                            maxCreditsPerCall = perCall.toIntOrNull() ?: 0,
                                        ),
                                    )
                                },
                                onSuccess = {
                                    editingBudget = false
                                    refreshKey++
                                    message = null
                                },
                                onFailure = { message = it.message },
                            )
                            LButton(t("app.settings.agents.agent_id.cancel", "Cancel"), { editingBudget = false }, variant = LButtonVariant.GHOST)
                        }
                    }
                }
            }

            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                    LSectionTitle(t("app.settings.agents.agent_id.expiry", "Expiry"))
                    AgentDetailNumbers("Expires ${agent.expiresAt.take(10)}")
                    AgentDetailMuted(t("app.settings.agents.agent_id.there_is_no_never_a_credential_2", "There is no \"never\". A credential with no end date outlives the reason it was created; renewing is one click by somebody who is still here."))
                    AgentDetailAction(
                        pageClaim = agentClaim,
                        label = t("app.settings.agents.agent_id.renew_for_30_days", "Renew for 30 days"),
                        variant = LButtonVariant.SECONDARY,
                        enabled = !busy,
                        busy = busy,
                        setBusy = { busy = it },
                        request = { workspaceSlug ->
                            ApiClient.updateAgentLimits(
                                workspaceSlug,
                                agent.id,
                                UpdateAgentLimitsRequest(
                                    creditBudgetMonthly = agent.creditBudgetMonthly,
                                    creditBudgetDaily = agent.creditBudgetDaily,
                                    maxCreditsPerCall = agent.maxCreditsPerCall,
                                    renewForDays = 30,
                                ),
                            )
                        },
                        onSuccess = {
                            refreshKey++
                            message = null
                        },
                        onFailure = { message = it.message },
                    )
                }
            }

            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                    LSectionTitle(t("app.settings.agents.agent_id.activity", "Activity"))
                    AgentDetailMuted(
                        tf(
                            "app.settings.agents.agent_id.created_date",
                            "Created {date}.",
                            "date" to agent.createdAt.take(10),
                        ),
                    )
                    AgentDetailMuted(
                        agent.lastUsedAt?.let {
                            tf(
                                "app.settings.agents.agent_id.last_used_date",
                                "Last used {date}.",
                                "date" to it.take(10),
                            )
                        }
                            // Not "never used" as a failure: a connection created this morning
                            // for next week's pipeline is fine, and the page should not imply
                            // otherwise.
                            ?: t("app.settings.agents.agent_id.never_used_yet", "Never used yet."),
                    )
                    Link(path = "/app/settings/agents/$agentId/log") { Text(t("app.settings.agents.agent_id.every_call_including_refusals", "Every call, including refusals")) }
                }
            }

            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    LSectionTitle(t("app.settings.agents.agent_id.stop_it", "Stop it"))
                    Row(Modifier.gap(Space.SM)) {
                        AgentDetailAction(
                            pageClaim = agentClaim,
                            label = if (agent.paused) {
                                t("app.settings.agents.agent_id.resume", "Resume")
                            } else {
                                t("app.settings.agents.agent_id.pause", "Pause")
                            },
                            variant = LButtonVariant.SECONDARY,
                            enabled = !busy,
                            busy = busy,
                            setBusy = { busy = it },
                            request = { workspaceSlug ->
                                ApiClient.pauseAgent(workspaceSlug, agent.id, !agent.paused)
                            },
                            onSuccess = {
                                refreshKey++
                                message = null
                            },
                            onFailure = { message = it.message },
                        )
                        AgentDetailAction(
                            pageClaim = agentClaim,
                            label = t("app.settings.agents.agent_id.rotate_credential", "Rotate credential"),
                            variant = LButtonVariant.SECONDARY,
                            enabled = !busy,
                            busy = busy,
                            setBusy = { busy = it },
                            request = { workspaceSlug -> ApiClient.rotateAgent(workspaceSlug, agent.id) },
                            onSuccess = {
                                rotated = it.token
                                // A new secret is not the one that was copied.
                                copied = false
                                refreshKey++
                                message = null
                            },
                            onFailure = { message = it.message },
                        )
                        if (confirmingRevoke) {
                            AgentDetailAction(
                                pageClaim = agentClaim,
                                label = t("app.settings.agents.agent_id.yes_revoke_it", "Yes, revoke it"),
                                variant = LButtonVariant.GHOST,
                                enabled = !busy,
                                busy = busy,
                                setBusy = { busy = it },
                                request = { workspaceSlug -> ApiClient.revokeAgent(workspaceSlug, agent.id) },
                                onSuccess = {
                                    message = null
                                    ctx.router.navigateTo("/app/settings/agents")
                                },
                                onFailure = { message = it.message },
                            )
                            // Arming had no way back: `confirmingRevoke` reset only on success or
                            // on a remount, so a reader who armed this and thought better of it was
                            // left holding a live "Yes, revoke it" until they reloaded the page.
                            LButton(
                                t("app.settings.agents.agent_id.cancel", "Cancel"),
                                { confirmingRevoke = false },
                                variant = LButtonVariant.GHOST,
                                enabled = !busy,
                            )
                        } else {
                            LButton(
                                t("app.settings.agents.agent_id.revoke", "Revoke"),
                                { confirmingRevoke = true },
                                variant = LButtonVariant.GHOST,
                                enabled = !busy,
                            )
                        }
                    }
                    AgentDetailMuted(
                        if (confirmingRevoke) {
                            t("app.settings.agents.agent_id.revoking_is_final_and_takes_the", "Revoking is final and takes the scopes and budgets with it. If this is a leak, rotate instead — same limits, new secret.")
                        } else {
                            t("app.settings.agents.agent_id.pausing_stops_it_without_destroying_the", "Pausing stops it without destroying the record of what it was.")
                        },
                    )
                }
            }
        }
    }
    }
}

/**
 * Now, as the same ISO-8601 string the server sends.
 *
 * Compared as text rather than parsed: both sides are UTC instants in the same format, and a
 * lexicographic comparison of those is exactly a chronological one — with no timezone library and no
 * chance of a parse that quietly succeeds in the wrong zone.
 */
private fun nowIso(): String = kotlin.js.Date().toISOString()

/**
 * What one scope on a connection means, in the catalogued frame the form that created it uses.
 *
 * `"${it.id} — ${it.consequence}"` is one expression to the extractor and no string at all, so the
 * em-dash join was fixed in English word order on the page where somebody decides whether to trust
 * a credential. `TokenScope.consequence` itself still comes from `:shared` in English — lifting it
 * into a composable lookup is a cross-module change this task does not own.
 */
internal fun agentScopeLine(scope: TokenScope): String = trf(
    "app.settings.agents.agent_id.scope_and_consequence",
    "{scope} — {consequence}",
    "scope" to scope.id,
    "consequence" to scope.consequence,
)
